import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from "react";
import lightbulb from "../../assets/images/icons/lightbulb-solution.png";
import NamePlanning from "./components/sections/name-planning";
import MonthlyAverage from "./components/sections/monthly-average";
import VariableIndirectCosts from "./components/sections/variable-indirect-costs";
import RouterPrompt from "../../components/router-prompt";
import TotalInvestment from "./components/sections/total-investment";
import ReceiptDeadline from "./components/sections/receipt-deadline";
import PaymentTerms from "./components/sections/payment-terms";
import InventoryTurnover from "./components/sections/inventory-turnover";
import ApiPlannings from "../../services/planning";
import {
  convertObjectValuesToFloat,
  eventTag,
  getCurrentCNPJ,
  getCurrentCompany,
  getProgressPercentageOfCompletion,
  sumValuesPercent,
  truncateText,
} from "../../services/utils";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import Loading from "../../components/loading";
import ToastContent from "../../components/toastContent";
import {toast} from "react-toastify";
import iconSuccess from "../../assets/images/icons/check-circle.png";
import iconError from "../../assets/images/icons/exclamation-octagon.png";
import ProjectedCashFlow from "./components/sections/projected-cash-flow";
import Results from "./components/sections/results";
import {BaSeButton, BaSeCheckbox, BaSeParagraph} from "@base/react";
import ModalDefault from "../../components/modals/modal-default";
import {AppContext} from "../../context/app";
import StepsNavigation from "./components/steps";
import Breadcrumb from "../../components/breadcrumb";
import {
  deepCompare,
  formatPlanningDataForSave,
  hasZeroValue,
  isStepCompleted,
  transformDataToFormModel,
  updateNestedKey,
  validateInventoryTurnover,
  validateSum100,
} from "./components/functions";
import {useDebouncedCallbackForm} from "../../hooks/use-debounce-callback-form";
import AvatarGroup from "../../components/card-status/avatar-group";
import {getUsersWithPlanner} from "../../mocks/helpers";
import BusinessAccordion from "./businessAccordion";
import ModalResult from "../../components/modals/modal-result";
import BodyResult from "./components/body-result";
import "./index.scss";

const apiPlannings = ApiPlannings();
const stateDefaultForm = {
  id: null,
  cnpj: null,
  progress_percentage: 0,
  step1: {
    title: "",
    // objective: "",
    score: "",
  },
  step2: {
    cif: "",
    cmp: "",
    cms: "",
    rtp: "",
    rts: "",
  },
  step3: {
    cc: {
      pdt: "",
      pdrt: "",
    },
    ci: {
      pdt: "",
      pdrt: "",
    },
    ou: {
      pdt: "",
      pdrt: "",
    },
    tcc: {
      pdt: "",
      pdrt: "",
    },
    tcd: {
      pdt: "",
      pdrt: "",
    },
  },
  step4: {
    cgp: "",
    iif: "",
  },
  step5: [],
  step6: [],
  step7: [{ order: 0, days: "", value: 0 }],
  step8: {
    VGdQV: 0,
    VGdPV: 0,
    VGdQV_just: "",
    VGdPV_just: "",
    goal_date: null,
  },
  step9: null,
  // step10: {
  //   financingValue: 0,
  //   guaranteesValue: 0,
  //   company_size: 99,
  //   percent: 0.8,
  // }
};

const event = {
  name: "planning",
  category: "planejamento",
  action: "click",
}
const eventRecommendations = {
  name: "recommendations",
  category: "recommendations",
  action: "click",
}

const Planning = () => {
  const location = useLocation();
  const { companies } = useContext(AppContext);

  const segments = location.pathname.split('/');
  const finalizedPlanning = segments[segments.length - 1] === 'resultados';
  const company = getCurrentCompany(companies);
  const accordionRef = useRef();
  const { idEdit, step } = useParams();
  const [atEnd, setAtEnd] = useState(false);
  const [isInventoryLow, setIsInventoryLow] = useState(false);
  const [initialForm, setInitialForm] = useState(stateDefaultForm);
  const [form, setForm] = useState(stateDefaultForm);
  const [role, setRole] = useState("");
  // const [dataPDF, setDataPDF] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [targetStep, setTargetStep] = useState(null);
  const breadcrumb = [
    { label: "Início", href: "/planejamentos" },
    {
      label: initialForm.step1?.title
        ? truncateText(initialForm.step1?.title, 20)
        : "Novo Planejamento",
    },
  ];
  const [shouldAutoSave, setShouldAutoSave] = useState(false);
  const [hasToast, setHasToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const [idPlanning, setIdPlanning] = useState(null);
  const [currentCnpj, setCurrentCnpj] = useState(null);
  const [openDialogNFCG, setOpenDialogNFCG] = useState(false);
  const [openDialogPrompt, setOpenDialogPrompt] = useState(false);
  const [openDialogFinish, setOpenDialogFinish] = useState(false);
  const [showModalOnSubmit, setShowModalOnSubmit] = useState(false);
  const [checkboxNotShow, setCheckboxNotShow] = useState(false);
  const [hasFlow, setHasFlow] = useState(false);
  const [showModalZeroedValues, setShowModalZeroedValues] = useState({
    show: false,
    doNotShow: false,
  });
  const [accordionData, setAccordionData] = useState({
    5: { show: false },
    6: { show: false },
    7: { show: false },
  });
  const [loadingPDFDownload, setLoadingPDFDownload] = useState(false);
  const [hasVisitedResults, setHasVisitedResults] = useState(false);
  const navigate = useNavigate();
  const forceUpdateForm = async () => {
    try {
      const res = await apiPlannings.getPlanning(idPlanning);
      const { data } = res;
      structureDataGeneral(data);
      return data;
    } catch (err) {
      console.error("ERROR => ", err);
    }
  };
  const [sendSaveForm] = useDebouncedCallbackForm(
    (finalizePlanning = false, onSuccessCallback) => {
      const company = getCurrentCNPJ(companies) || null;
      const data = formatPlanningDataForSave(form, currentStep);
      const nameStep = sectionsList.find(
        (item) => item.index === currentStep
      )?.title;

      setDisableButtons(true);
      if (finalizePlanning || currentStep === 9) {
        setLoading(true);
      }
      if (finalizePlanning) {
        setOpenDialogFinish(false);
        data.is_finished = finalizePlanning;
        data.progress_percentage = getProgressPercentageOfCompletion(data?.data?.lastStepSaved, finalizePlanning)
      }
      if (!idPlanning) {
        data.cnpj = company || null;
      }

      const request = idPlanning
        ? apiPlannings.updatePlanning(data, idPlanning)
        : apiPlannings.createPlanning(data);

      const successMessage = finalizePlanning
        ? "Planejamento salvo."
        : idPlanning
        ? `Suas informações de <strong>${
            currentStep === 1 ? "Nome do planejamento" : nameStep
          }</strong> foram salvas.`
        : "Planejamento criado.";

      request
        .then((res) => handleSuccess(res, successMessage, onSuccessCallback))
        .catch(handleError)
        .finally(() => {
          setLoading(false);
          setDisableButtons(false);
        });
    },
    700
  );

  const isPlanner = form.planner?.is_planner;

  const handleInventoryAlert = useCallback((isBelowThreshold) => {
    setIsInventoryLow(isBelowThreshold);
  }, []);
  const handleShowAccordion = useCallback((step, show) => {
    setAccordionData((prev) => ({ ...prev, [step]: { ...prev[step], show } }));
  }, []);

  const structureDataGeneral = (data) => {
    const model = transformDataToFormModel(data);
    setForm(model);
    const clone = JSON.parse(JSON.stringify(model));
    setInitialForm(clone);
  };
  const handleSuccess = async (res, message, callback) => {
    const { data } = res;
    structureDataGeneral(data);
    setIdPlanning(data.id);

    if (data?.is_finished) {
      setTimeout(() => {
        setOpenDialogNFCG(true);
      }, 1500);
    }

    if (!hasToast) {
      toast.success(<ToastContent title="Sucesso!" description={message} />, {
        icon: () => <img src={iconSuccess} alt="icon" />,
        onOpen: () => setHasToast(true),
        onClose: () => setHasToast(false),
        autoClose: 5000,
        pauseOnFocusLoss: false,
      });
    }

    if (callback) {
      callback();
    }

    eventTag(event?.name, event?.category, "submit", message);
  };
  const handleError = (err) => {
    console.error("ERROR => ", err);
    const errorResponse = err.response?.data?.error;
    const errorMessage = errorResponse
      ? "Desculpe. Já existe outro planejamento com esse nome salvo."
      : "Houve uma falha no sistema. Tente novamente.";

    toast.error(<ToastContent title="Falha!" description={errorMessage} />, {
      icon: () => <img src={iconError} alt="icon" />,
      autoClose: 5000,
      pauseOnFocusLoss: false,
    });

    setLoading(false);
  };
  const handleCheckHasChanges = (baseData = form) => {
    if (finalizedPlanning) return false;
    const data = formatPlanningDataForSave(baseData, currentStep);
    const initialFormatted = formatPlanningDataForSave(
      initialForm,
      initialForm.lastStepSaved
    );

    return !deepCompare(data, initialFormatted);
  };
  const handleChangeStep = (e) => {
    if (e <= form.lastStepSaved) {
      if (handleCheckHasChanges() && role !== "visualizador" && !finalizedPlanning) {
        const stepData = form[`step${currentStep}`];
        const shouldValidateSum = currentStep >= 5 && currentStep <= 7;

        let isStepValid = true;

        if (shouldValidateSum) {
          isStepValid =
            currentStep === 7
              ? validateInventoryTurnover(stepData)
              : validateSum100(stepData);
        }

        if (isStepValid) {
          sendSaveForm(false, () => setCurrentStep(e));
        } else {
          if (shouldValidateSum) {
            setTargetStep(e);
            setOpenDialogPrompt(true);
          }
        }
      } else {
        setCurrentStep(e);
      }
    }
  };
  const handleNext = (ignoreZero = false) => {
    if (currentStep > 1 && currentStep <= 4 && role !== "visualizador") {
      const data = form[`step${currentStep}`];

      if (
        !form.is_finished &&
        !ignoreZero &&
        !showModalZeroedValues.doNotShow &&
        hasZeroValue(data)
      ) {
        setShowModalZeroedValues((prev) => ({
          ...prev,
          show: true,
        }));
      } else {
        setShowModalZeroedValues((prev) => ({
          ...prev,
          show: false,
        }));
        if (handleCheckHasChanges()) {
          sendSaveForm(false, () => setCurrentStep((prev) => prev + 1));
        } else {
          setCurrentStep((prev) => prev + 1);
        }
      }
    } else {
      if (handleCheckHasChanges() && role !== "visualizador") {
        sendSaveForm(false, () => setCurrentStep((prev) => prev + 1));
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };
  const handleBack = (forceBack = false) => {
    if (
      !forceBack &&
      currentStep >= 5 &&
      currentStep <= 7 &&
      handleCheckHasChanges() &&
      role !== "visualizador"
    ) {
      setOpenDialogPrompt(true);
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };
  const downloadPDF = async () => {
    // const sections = document.querySelectorAll("#pdf-content section");
    // if (!sections.length) return;
    // const pdf = new jsPDF("p", "mm", "a4");
    // const pageWidth = pdf.internal.pageSize.getWidth();
    // const pageHeight = pdf.internal.pageSize.getHeight();
    // const marginX = 10;
    // const topMargin = 10;
    // const bottomMargin = 20;
    // const usableWidthMm = pageWidth - 2 * marginX;

    // const usableHeightMm = pageHeight - topMargin - bottomMargin;

    // for (let i = 0; i < sections.length; i++) {
    //   const section = sections[i];
    //   const canvas = await html2canvas(section, { scale: 2, useCORS: true });
    //   const imgWidthPx = canvas.width;
    //   const imgHeightPx = canvas.height;

    //   const mmPerPx = usableWidthMm / imgWidthPx;
    //   const pageSliceHeightPx = usableHeightMm / mmPerPx;

    //   let offsetYpx = 0;
    //   const overlapPx = 25;

    //   while (offsetYpx < imgHeightPx) {
    //     const sliceHeightPx = Math.min(
    //       pageSliceHeightPx,
    //       imgHeightPx - offsetYpx
    //     );

    //     const sliceCanvas = document.createElement("canvas");
    //     sliceCanvas.width = imgWidthPx;
    //     sliceCanvas.height = sliceHeightPx;

    //     const ctx = sliceCanvas.getContext("2d");
    //     ctx.drawImage(canvas, 0, -offsetYpx);

    //     const sliceImgData = sliceCanvas.toDataURL("image/png");
    //     const sliceHeightMm = sliceHeightPx * mmPerPx;

    //     pdf.addImage(
    //       sliceImgData,
    //       "PNG",
    //       marginX,
    //       topMargin,
    //       usableWidthMm,
    //       sliceHeightMm
    //     );

    //     const isLastSlice = offsetYpx + sliceHeightPx >= imgHeightPx;
    //     const overlapMm = overlapPx * mmPerPx;

    //     if (!isLastSlice) {
    //       const maskY = topMargin + usableHeightMm - overlapMm;
    //       pdf.setFillColor(255, 255, 255);
    //       pdf.rect(0, maskY, pageWidth, overlapMm, "F");
    //     }

    //     offsetYpx += pageSliceHeightPx - overlapPx;

    //     if (offsetYpx < imgHeightPx) {
    //       pdf.addPage();
    //     }
    //   }

    //   if (i < sections.length - 1) {
    //     pdf.addPage();
    //   }
    // }

    // pdf.save("Relatorio.pdf");
    await markDownloadDashboard();
    eventTag(event?.name, event?.category, "download", "Download planejamento");
  };
  const finishPlanning = () => {
    const data = formatPlanningDataForSave(form, currentStep);

    if (!data?.data?.goal_date) {
      setShowModalOnSubmit(true);
    } else {
      setOpenDialogFinish(true);
    }
  };
  const handleUpdateStep = (autoSave = false, step, keyOrValue, value) => {
    setForm((prev) => {
      const isArray = Array.isArray(prev[step]);
      if (isArray || step === "step9") {
        return {
          ...prev,
          [step]: keyOrValue,
        };
      } else {
        return {
          ...prev,
          [step]: updateNestedKey(prev[step] || {}, keyOrValue, value),
        };
      }
    });
    setShouldAutoSave(autoSave);
  };
  const sectionsList = [
    {
      index: 1,
      title: form.step1?.title ? form.step1?.title : "Novo Planejamento",
      isCompleted: isStepCompleted(form.step1, ["score"]),
      content: (
        <NamePlanning
          data={form.step1}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(key, value) =>
            handleUpdateStep(false, "step1", key, value)
          }
        />
      ),
    },
    {
      index: 2,
      title: "Média Mensal",
      isCompleted: isStepCompleted(form.step2, ["*"]),
      content: (
        <MonthlyAverage
          data={form.step2}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(key, value) => handleUpdateStep(true, "step2", key, value)}
        />
      ),
    },
    {
      index: 3,
      title: "Custos Indiretos Variáveis",
      isCompleted: isStepCompleted(form.step3, ["*"]),
      content: (
        <VariableIndirectCosts
          data={form.step3}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(key, value) => handleUpdateStep(true, "step3", key, value)}
        />
      ),
    },
    {
      index: 4,
      title: "Investimento Total",
      isCompleted: isStepCompleted(form.step4, ["*"]),
      content: (
        <TotalInvestment
          data={form.step4}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(key, value) => handleUpdateStep(true, "step4", key, value)}
        />
      ),
    },
    {
      index: 5,
      title: "Prazos de Recebimento",
      isCompleted: isStepCompleted(form.step5, [], { requireSum100: true }),
      content: (
        <ReceiptDeadline
          data={form.step5}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(value) => handleUpdateStep(true, "step5", value)}
          showAccordion={(show) => handleShowAccordion(5, show)}
        />
      ),
    },
    {
      index: 6,
      title: "Prazos de Pagamento",
      isCompleted: isStepCompleted(form.step6, [], { requireSum100: true }),
      content: (
        <PaymentTerms
          data={form.step6}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(value) => handleUpdateStep(true, "step6", value)}
          showAccordion={(show) => handleShowAccordion(6, show)}
        />
      ),
    },
    {
      index: 7,
      title: "Giro de Estoque",
      isCompleted: isStepCompleted(form.step7, [], { inventoryTurnover: true }),
      content: (
        <InventoryTurnover
          data={form.step7}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(value) => handleUpdateStep(true, "step7", value)}
          onInventoryBelowThreshold={handleInventoryAlert}
          showAccordion={(show) => handleShowAccordion(7, show)}
        />
      ),
    },
    {
      index: 8,
      title: "Resultados",
      isCompleted: isStepCompleted(form.step8, ["*"]),
      content: (
        <Results
          data={form.step8}
          results={form.results}
          event={event}
          idPlanning={idPlanning}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(key, value) => handleUpdateStep(true, "step8", key, value)}
          goToFLow={() => setCurrentStep(9)}
        />
      ),
    },
    {
      index: 9,
      title: "Capacidade de Pagamento",
      subtitle: "(Fluxo de Caixa Projetado)",
      isCompleted: isStepCompleted(form.step9, ["*"]),
      content: (
        <ProjectedCashFlow
          data={form.step9}
          results={form.results}
          isDisabled={form.is_finished || role === "visualizador"}
          onChange={(value) => handleUpdateStep(true, "step9", value)}
          planId={idPlanning}
          isStep9={currentStep}
          forceUpdateForm={forceUpdateForm}
          event={event}
        />
      ),
    },
  ];
  const scrollToGoalDate = () => {
    setShowModalOnSubmit(false);
    setCurrentStep(8);

    setTimeout(() => {
      const element = document.getElementById("goalDate");
      if (element) {
        const offsetTop = element.getBoundingClientRect().top + window.scrollY;
        const offset = 70;

        window.scrollTo({
          top: offsetTop - offset,
          behavior: "smooth",
        });
      }
    }, 1000);
  };
  const TemplateButtons = () => {
    const buttonPrev = (
      <BaSeButton
        type="secondary"
        isBold={true}
        buttonType="button"
        onClick={() => {
          handleBack();
          eventTag(event?.name, event?.category, event?.action, "Anterior");
        }}
        color="#2E5DE6"
        size="medium"
        value="Anterior"
        width="auto"
        isDisabled={disableButtons}
      />
    );
    const buttonNext = (
      <BaSeButton
        isBold
        buttonType="button"
        color="#2E5DE6"
        size="medium"
        value="Próximo"
        width="auto"
        isDisabled={
          disableButtons ||
          (role !== "visualizador" &&
            currentStep <= 7 &&
            !form.is_finished &&
            !sectionsList.find((step) => step.index === currentStep)
              ?.isCompleted)
        }
        onClick={async () => {
          if (role !== "visualizador" && currentStep === 7 && isInventoryLow) {
            setOpenDialogPrompt(true);
            return;
          }
          if (document.activeElement) {
            document.activeElement.blur();
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          handleNext();
          eventTag(event?.name, event?.category, event?.action, "Próximo");
        }}
      />
    );
    const buttonFinish = (
      <BaSeButton
        isBold
        buttonType="button"
        color="#2E5DE6"
        size="medium"
        value="Finalizar Planejamento"
        width="auto"
        isDisabled={disableButtons || role === "visualizador"}
        onClick={() => {
          finishPlanning();
          eventTag(
            event?.name,
            event?.category,
            event?.action,
            "Finalizar Planejamento"
          );
        }}
      />
    );
    const buttonDownload = (
      <BaSeButton
        isBold
        buttonType="button"
        color="#2E5DE6"
        size="medium"
        leftIcon="download-alt"
        value="Baixar Planejamento"
        width="auto"
        isDisabled={disableButtons}
        isLoading={loadingPDFDownload}
        loadingText="Carregando..."
        onClick={() => {
          downloadPDF();
          eventTag(
            event?.name,
            event?.category,
            event?.action,
            "Baixar Planejamento"
          );
        }}
      />
    );

    const resolveRightButton = () => {
      if (currentStep <= 1)   return null;
      if (form.is_finished)   return buttonDownload;
      if (currentStep === 8)  return hasFlow ? buttonFinish : buttonNext;
      if (currentStep > 8)    return buttonFinish;
      return buttonNext;
    };

    const showPrev = currentStep > 1;
    const rightButton = resolveRightButton();

    return (
      <div className="template-buttons-wrapper">
        {showPrev && buttonPrev}
        {rightButton ?? buttonNext}
      </div>
    );
  };
  const markDownloadDashboard = async () => {
    try {
      setLoadingPDFDownload(true)
      const res = await apiPlannings.markDownloadDashboard(idPlanning);

      const fetchRes = await fetch(res.data.pdf);
      const blob = await fetchRes.blob();

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Planejamento-${idPlanning}.pdf`;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn(error);
      toast.error(
        <ToastContent
          title="Falha!"
          description={
            error?.message || "Houve uma falha no sistema. Tente novamente."
          }
        />,
        {
          icon: () => <img src={iconError} alt="icon" />,
          autoClose: 5000,
          pauseOnFocusLoss: false,
        }
      );
    } finally {
      setLoadingPDFDownload(false)
    }
  };
  const discardChanges = () => {
    const step = `step${currentStep}`;
    setForm((prev) => {
      return {
        ...prev,
        [step]: initialForm[step],
      };
    });
    setOpenDialogPrompt(false);

    if (targetStep) {
      setCurrentStep(targetStep);
      setTargetStep(null);
    } else {
      handleBack(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      const scrollPercent = ((scrollTop + windowHeight) / docHeight) * 100;
      setAtEnd(scrollPercent >= 93);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (shouldAutoSave && handleCheckHasChanges() && role !== "visualizador" && !finalizedPlanning) {
      const stepData = form[`step${currentStep}`];
      const shouldValidateSum = currentStep >= 5 && currentStep <= 7;

      let isStepValid = true;

      if (shouldValidateSum) {
        isStepValid =
          currentStep === 7
            ? validateInventoryTurnover(stepData)
            : validateSum100(stepData);
      }

      if (isStepValid) {
        sendSaveForm();
      }
    }

    setHasFlow(!!form?.step9);
    // eslint-disable-next-line
  }, [form]);

  const formatDefaultForm = (response) => {
    const { data } = response;
    return {
      cnpj: data.cnpj || null,
      title: data.title,
      data: data.data,
      fampe: data?.fampe,
      pdf_url: data.pdf_url,
      is_finished: data.is_finished,
      results: data.results,
    };
  };
  const forceUpdateLastStepSaved = (step, id, current) => {
    const company = getCurrentCNPJ(companies) || null;

    apiPlannings
      .updatePlanning(
        {
          cnpj: company || null,
          ...current,
          data: {
            ...current?.data,
            lastStepSaved: step,
          },
          progress_percentage: getProgressPercentageOfCompletion(step)
        },
        id
      )
      .then((res) => {
        const { data } = res;
        structureDataGeneral(data);
        setIdPlanning(data.id);
      })
      .catch((err) => {
        console.error("ERROR => ", err);
        toast.error(
          <ToastContent
            title="Falha!"
            description="Houve uma falha no sistema. Tente novamente."
          />,
          {
            icon: () => <img src={iconError} alt="icon" />,
            autoClose: 5000,
            pauseOnFocusLoss: false,
          }
        );
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (currentStep === 9 && form?.lastStepSaved !== 9 && idPlanning) {
      const dataToSave = formatPlanningDataForSave(form, 9);
      forceUpdateLastStepSaved(9, idPlanning, dataToSave);
    }

    // eslint-disable-next-line
  }, [currentStep, form?.lastStepSaved, idPlanning]);

  const plannerData = useMemo(() => {
    if (!form?.planner) return null;

    return {
      name: form.planner.name_planner || "",
      role: "Planejador",
    };
  }, [form?.planner]);

  const allUsers = useMemo(() => {
    return getUsersWithPlanner(form.invites, plannerData);
  }, [form.invites, plannerData]);
  const handleRemovedUser = (userId) => {
    setForm((prev) => ({
      ...prev,
      invites: prev.invites.filter((invite) => invite.id !== userId),
    }));
  };

  useEffect(() => {
    if (idEdit) {
      setLoading(true);
      setDisableButtons(true);
      apiPlannings
        .getPlanning(idEdit)
        .then((res) => {
          const { data } = res;
          structureDataGeneral(data);
          setIdPlanning(data.id);
          setRole(data.role);
          const dataJson = data?.data

          if (dataJson?.lastStepSaved === undefined) {
            let stepSaved = 1;
            // checkSteps
            if (dataJson?.pdr && Object.values(dataJson.pdr).length) {
              const values = convertObjectValuesToFloat(dataJson.pdr);
              const sum = sumValuesPercent(values) === 100;

              if (sum) stepSaved = 5;
            }

            if (dataJson?.pdc && Object.values(dataJson.pdc).length) {
              const values = convertObjectValuesToFloat(dataJson.pdc);
              const sum = sumValuesPercent(values) === 100;

              if (sum) stepSaved = 6;
            }

            if (dataJson?.gde && Object.values(dataJson.gde).length) {
              const values = convertObjectValuesToFloat(dataJson.gde);
              const sum = sumValuesPercent(values) === 100;

              if (sum) stepSaved = 7;
            }

            if (dataJson?.fluxo && Object.values(dataJson.fluxo).length) {
              stepSaved = 9;
            }

            forceUpdateLastStepSaved(
              stepSaved,
              data.id,
              formatDefaultForm(res)
            );
          }
          if (!!data?.results?.fluxo) {
            forceUpdateLastStepSaved(9, data.id, formatDefaultForm(res));
          }
        })
        .catch((data) => {
          console.error("ERROR => ", data);
          toast.error(
            <ToastContent
              title="Falha!"
              description="Houve uma falha no sistema. Tente novamente."
            />,
            {
              icon: () => <img src={iconError} alt="icon" />,
              autoClose: 5000,
              pauseOnFocusLoss: false,
            }
          );
          if (data?.response) {
            const { status } = data?.response;
            if (status === 404) {
              setTimeout(() => {
                window.history.back();
              }, 2000);
            }
          }
        })
        .finally(() => {
          setLoading(false);
          setDisableButtons(false);
        });
    }
    // eslint-disable-next-line
  }, [idEdit, step]);
  useEffect(() => {
    if (companies.length) {
      const activeCompany = companies.find((el) => el.isActive && el.cnpj);
      if (activeCompany) {
        if (currentCnpj && activeCompany.cnpj !== currentCnpj) {
          navigate("/meus-planejamentos");
        }
        setCurrentCnpj(activeCompany.cnpj);
      }
    }
    // eslint-disable-next-line
  }, [companies, currentCnpj, navigate]);
  useEffect(() => {
    if (!showModalZeroedValues.show && checkboxNotShow) {
      setShowModalZeroedValues({
        show: false,
        doNotShow: true,
      });
    }
    // eslint-disable-next-line
  }, [showModalZeroedValues.show]);

  const [isOpen, setIsOpen] = useState(false);

  const modalResultOpen = () => {
    eventTag(eventRecommendations?.name, eventRecommendations?.category, eventRecommendations?.action, "Abrir recomendações")
    setIsOpen(true);
  };
  const modalResultClose = () => {
    eventTag(eventRecommendations?.name, eventRecommendations?.category, eventRecommendations?.action, "Fechar recomendações")
    setIsOpen(false);
  };

  const [dataRecommendations, setDataRecommendations] = useState([]);
  const urlCompleta = window.location.href;
  const urlsParts = urlCompleta.split("/");
  const id = urlsParts[4] || idPlanning;

  const recommendations = async () => {
    try {
      const { data } = await apiPlannings.getRecommendations(id);
      eventTag(eventRecommendations?.name, eventRecommendations?.category, "submit", "Buscar recomendações")
      setDataRecommendations(data || []);
    } catch (error) {
      console.warn(error);
    }
  };
  useEffect(() => {
    if (currentStep === 8) {
      setHasVisitedResults(true);
    }
  }, [currentStep]);
  useEffect(() => {
    recommendations()

    // eslint-disable-next-line
  }, [form.results]);

  const startMonitoring = () => {
    setLoading(true);
    setOpenDialogNFCG(false);
    apiPlannings
      .monitorPlanning(id)
      .then((res) => {
        const { data } = res;
        toast.success(
          <ToastContent
            title="Sucesso!"
            description="Monitoramento iniciado com sucesso."
          />,
          {
            autoClose: 5000,
            pauseOnFocusLoss: false,
          }
        );
        eventTag(event?.name, "post_credit", "submit", `Iniciando monitoramento,: ${data?.title}`)

        navigate(`/planejamento/${data.id}/pos-credito`);
      })
      .catch((err) => {
        if (err.message.includes("Planejamento já monitorado")) {
          toast.error(
            <ToastContent
              title="Erro"
              description="Este planejamento já está sendo monitorado."
            />,
            {
              autoClose: 5000,
              pauseOnFocusLoss: false,
            }
          );
        } else {
          toast.error(
            <ToastContent
              title="Erro"
              description="Não foi possível realizar a operação."
            />,
            {
              autoClose: 5000,
              pauseOnFocusLoss: false,
            }
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div translate="no" className="planning">
      <ModalDefault
        callBackClose={scrollToGoalDate}
        show={showModalOnSubmit}
        title="Estabeleça sua meta!"
      >
        <BaSeParagraph>
          Para finalizar e baixar o planejamento, por favor preencha a Data da
          meta planejamento na tela de <strong> Resultados.</strong>
        </BaSeParagraph>{" "}
        <br />
        <BaSeButton
          value="Ok"
          onClick={() => {
            scrollToGoalDate();
            eventTag(event?.name, event?.category, event?.action, "Ok");
          }}
          width="71px"
        />
      </ModalDefault>

      <div className="planning__content">
        <div className="planning__content--left">
          <div className="planning__wrapper--left">
            <div className="planning__breadcrumb">
              <Breadcrumb items={breadcrumb} />
            </div>
            {/*<CardScore*/}
            {/*  step={currentStep}*/}
            {/*  planning={form}*/}
            {/*  isDisabled={form.is_finished || role === "visualizador"}*/}
            {/*/>*/}
            <StepsNavigation
              data={sectionsList}
              currentStep={currentStep}
              isDisabled={disableButtons}
              setCurrentStep={(e) => handleChangeStep(e)}
              lastStepSaved={form?.lastStepSaved}
              event={event}
            />
           {(hasVisitedResults || finalizedPlanning) && dataRecommendations.length > 0 ? (
              <button
                className="btn-solution"
                onClick={() => modalResultOpen()}
              >
                Temos recomendações pra você!
                <div className="wrapper-icon">
                  <img src={lightbulb} alt="icon de lâmpada" />
                  <span className="notification">{dataRecommendations.length}</span>
                </div>
              </button>
            ) : ""}
          </div>
        </div>
        <div className="planning__content--right">
          <div className="planning__wrapper--right">
            <section className="planning__step">
              {(() => {
                const currentSection = sectionsList.find(
                  (step) => step.index === currentStep
                );
                if (!currentSection) return null;

                return (
                  <>
                    <h2 className="planning__step--title">
                      {currentSection?.index === 1
                        ? "Novo Planejamento"
                        : currentSection?.title}
                      <span>{currentSection?.subtitle}</span>
                    </h2>
                    {loading ? <Loading /> : currentSection?.content}
                  </>
                );
              })()}
            </section>
            {accordionData[currentStep]?.show &&
              currentStep >= 5 &&
              currentStep <= 7 && (
                <BusinessAccordion
                  step={currentStep}
                  planId={idPlanning}
                  balanceValues={form?.results?.balance_values}
                  onNext={async () => {
                    if (
                      role !== "visualizador" &&
                      currentStep === 7 &&
                      isInventoryLow
                    ) {
                      setOpenDialogPrompt(true);
                      return;
                    }
                    setLoading(true);
                    try {
                      const res = await apiPlannings.getPlanning(idPlanning);
                      const { data } = res;
                      structureDataGeneral(data);
                      handleNext();
                      eventTag(
                        event?.name,
                        event?.category,
                        event?.action,
                        "Próximo"
                      );
                    } catch (err) {
                      console.error("ERROR => ", err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabledFields={form.is_finished || role === "visualizador"}
                  ref={accordionRef}
                  forceUpdateForm={forceUpdateForm}
                />
              )}
          </div>
          <div className={`planning__footer ${atEnd ? " under-buttons" : ""}`}>
            {id &&
              <div
                className="id-holder"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(id)
                    toast.success(<ToastContent
                      title="Sucesso!"
                      description="Texto copiado para a área de transferência!"
                    />, {
                      icon: () => <img src={iconSuccess} alt="icon" />,
                      autoClose: 5000,
                      pauseOnFocusLoss: false,
                    })
                  } catch (err) {
                    console.error(err)
                  }
                }}
              >
                ID: {id}
              </div>
            }
            <div className="planning__footer-info">
              {form.isShared && allUsers.length > 0 && (
                <AvatarGroup
                  users={allUsers}
                  maxVisible={window.innerWidth <= 426 ? 1 : 2}
                  insidePlanning
                  isPlanner={isPlanner}
                  onUserRemoved={handleRemovedUser}
                  event={event}
                />
              )}
              <TemplateButtons />
            </div>
          </div>
        </div>
      </div>

      <RouterPrompt
        planningId={form?.id}
        checkForAbandonment={form?.id && form?.lastStepSaved < 8 && currentStep <= 7}
        hasUnsavedChanges={
          currentStep >= 5 && currentStep <= 7
            ? handleCheckHasChanges()
            : false
        }
        currentCnpj={form?.cnpj || null}
        forceShowDialog={currentStep >= 5 && currentStep <= 7 && openDialogPrompt}
        callbackCancel={() => setOpenDialogPrompt(false)}
        callbackConfirm={() => discardChanges()}
      />

      {!showModalZeroedValues.doNotShow ? (
        <ModalDefault
          title="Atenção!"
          callBackClose={() =>
            setShowModalZeroedValues((prev) => ({
              ...prev,
              show: false,
            }))
          }
          show={showModalZeroedValues.show}
          classCustom="dialog-alert zero"
        >
          <p className="dialog-alert__description">
            Campos não preenchidos serão contabilizados como{" "}
            <strong>”Zero”</strong> automaticamente.
            <br />
            <br />
            Para ter resultados mais precisos, preencha todas as informações
            disponíveis.
          </p>
          <div className="dialog-alert__checkbox">
            <BaSeCheckbox
              label="Não mostrar essa mensagem novamente"
              id="do_not_show"
              name="do_not_show"
              checked={checkboxNotShow}
              onChange={setCheckboxNotShow}
              color={"#0f438a"}
            />
          </div>
          <div className="dialog-alert__buttons">
            <BaSeButton
              type="secondary"
              buttonType="button"
              onClick={() => {
                setShowModalZeroedValues((prev) => ({
                  ...prev,
                  show: false,
                }));
                eventTag(
                  event?.name,
                  event?.category,
                  event?.action,
                  "Voltar para edição"
                );
              }}
              color="#2E5DE6"
              size="medium"
              value="Voltar para edição"
              width="auto"
            />
            <BaSeButton
              buttonType="button"
              color="#2E5DE6"
              size="medium"
              value="Continuar"
              width="auto"
              onClick={() => {
                handleNext(true);
                eventTag(
                  event?.name,
                  event?.category,
                  event?.action,
                  "Continuar"
                );
              }}
            />
          </div>
        </ModalDefault>
      ) : (
        ""
      )}

      <ModalResult
        show={isOpen}
        callBackClose={modalResultClose}
        title={"Recomendações"}
        enterprise={company?.name || ""}
      >
        <BodyResult data={dataRecommendations} eventRecommendations={eventRecommendations} call={recommendations} />
      </ModalResult>

      <ModalDefault
        title="Atenção!"
        callBackClose={() => setOpenDialogFinish(false)}
        show={openDialogFinish}
        classCustom="dialog-alert finish"
      >
        <p className="dialog-alert__description">
          Você está prestes a salvar este planejamento.
          <br />
          <br />
          Após a confirmação, não será possível editá-lo. No entanto, este
          planejamento ficará disponível para consulta posterior e poderá ser
          duplicado para novos planejamentos com base nas informações atuais.
          <br />
          <br />
          Deseja continuar?
        </p>
        <div className="dialog-alert__buttons">
          <BaSeButton
            type="secondary"
            buttonType="button"
            onClick={() => {
              setOpenDialogFinish(false);
              eventTag(
                event?.name,
                event?.category,
                event?.action,
                "Voltar para edição"
              );
            }}
            color="#2E5DE6"
            size="medium"
            value="Voltar para edição"
            width="auto"
          />
          <BaSeButton
            buttonType="button"
            color="#2E5DE6"
            size="medium"
            value="Finalizar"
            width="auto"
            isDisabled={form.is_finished}
            onClick={() => {
              sendSaveForm(true);
              eventTag(
                event?.name,
                event?.category,
                event?.action,
                "Finalizar"
              );
            }}
          />
        </div>
      </ModalDefault>

      <ModalDefault
        title="Parabéns pelo seu planejamento!"
        callBackClose={() => setOpenDialogNFCG(false)}
        show={openDialogNFCG}
        classCustom="dialog-alert-ncg finish"
      >
        {form?.results?.nfcg > 0 ? (
          <p className="dialog-alert__description">
            Identificamos que sua empresa pode se beneficiar de um{" "}
            <strong>reforço de capital de giro</strong>. Simule agora o uso de
            garantias com o <strong>FAMPE – solução do Sebrae</strong> que
            facilita o acesso ao crédito – e avançar com mais segurança.
          </p>
        ) : (
          <p className="dialog-alert__description">
            Sua empresa está com as finanças equilibradas e um bom fundo de
            reserva.
            <br />
            <br />
            Que tal aproveitar esse momento para investir ainda mais no seu
            negócio? Você pode simular um investimento com o apoio do{" "}
            <strong>FAMPE – solução do Sebrae</strong> que facilita o acesso ao
            crédito para pequenos negócios.
          </p>
        )}
        {form?.step9?.saidas?.length > 0 ? (
          <p
            className="dialog-alert__description"
            style={{ marginTop: "15px" }}
          >
            Além disso, este planejamento atende aos requisitos para ser considerado um <strong>Planejamento Base</strong> na sua <strong>jornada de monitoramento</strong>.
            <br />
            <br />
            Nessa jornada, você poderá acompanhar mensalmente seus indicadores de <strong>desempenho</strong> e <strong>endividamento</strong>, entendendo melhor a saúde do seu negócio e identificando oportunidades de melhoria.
          </p>
        ) : (
          ""
        )}

        <p className="dialog-alert__description" style={{ marginTop: "20px" }}>
          Como gostaria de prosseguir?
        </p>

        <div className="dialog-alert__buttons">
          <BaSeButton
            type="secondary"
            buttonType="button"
            onClick={() => setOpenDialogNFCG(false)}
            color="#2E5DE6"
            size="medium"
            value="Agora não"
            width="auto"
          />
          <BaSeButton
            buttonType="button"
            color="#2E5DE6"
            size="medium"
            value="Simular agora"
            width="auto"
            onClick={() => {
              navigate("/garantias");
            }}
          />
          {form?.step9?.saidas?.length > 0 ? (
            <BaSeButton
              buttonType="button"
              color="#2E5DE6"
              size="medium"
              value="Começar monitoramento"
              width="auto"
              onClick={() => startMonitoring()}
            />
          ) : (
            ""
          )}
        </div>
      </ModalDefault>

      {/* {dataPDF ? <PdfPage dataPlanning={dataPDF} /> : ""} */}
    </div>
  );
};

export default Planning;
